<template>
  <div :id="`tree_menu_${unit.addressId}`" class="tree-menu">
    <v-list-item
      v-if="shouldShowRow"
      v-long-press="(e) => onRowLongPress(e, unit)"
      class="label-wrapper"
      @click="toggleChildren"
      @contextmenu.prevent="activateMenu($event, unit)"
    >
      <div
        :style="!mobile ? indent : indentMobile"
        :class="labelClasses"
        class="d-flex flex-row flex-grow-1"
      >
        <div v-if="unit.type === 'unit'">
          <v-icon v-if="arrangeTreeMenu" small>mdi-equal-box</v-icon>
          <v-icon v-else small>mdi-grain</v-icon>
        </div>
        <v-icon v-else small>{{ iconClasses }}</v-icon>
        <div
          v-if="bedId"
          class="ma-0 mx-2 body-2 sp-unit d-flex flex-row flex-grow-1"
        >
          <v-chip
            v-show="$store.state.units.showUnitId"
            class="mr-2"
            color="red"
            outlined
            small
          >
            <v-icon small left> mdi-identifier </v-icon>
            {{ bedId }}
          </v-chip>

          <p class="pt-1">{{ unit.name + ' - &nbsp;' }}</p>
          <p class="pt-1">
            {{ getUnitUser() }}
          </p>

          <div class="flex-grow-1"></div>

          <div
            v-if="supplementaryData && supplementaryData.lease"
            class="d-flex flex-row"
          >
            <list-icon
              :show="supplementaryData.lease.status === 'expired'"
              icon="mdi-calendar-alert"
              title="The contract period has expired"
              color="red"
            ></list-icon>
            <list-icon
              :show="isOverdue()"
              icon="mdi-calendar-clock-outline"
              :title="`Rental is overdue ( ${getOverdueDate()})`"
              color="orange"
            ></list-icon>
            <list-icon
              :show="supplementaryData.hasUnpaidPaymentRequest"
              icon="mdi-clipboard-clock-outline"
              title="There are unpaid payment requests"
              color="pink"
            ></list-icon>
            <list-icon
              :show="supplementaryData.hasPendingApprovalPaymentRequest"
              icon="mdi-progress-check"
              title="There are pending payment request approvals"
              color="green"
            ></list-icon>
            <list-icon
              :show="supplementaryData.hasPendingApprovalPaidPayment"
              icon="mdi-check-decagram-outline"
              title="There are pending paid payment approvals"
              color="green"
            ></list-icon>
            <list-icon
              :show="supplementaryData.lease.preTerminated === true"
              icon="mdi-book-cancel-outline"
              title="The lease has been pre-terminated"
              color="warning"
            ></list-icon>
          </div>
        </div>
        <p
          v-else-if="unit.type === 'group'"
          :class="{
            'ma-0': !mobile,
            'mx-2': !mobile,
            'mx-1': mobile,
            'body-2': true,
            'font-weight-bold': true,
            'sp-group': true
          }"
        >
          {{ unit.name }}
        </p>
      </div>
    </v-list-item>
    <draggable
      v-show="showChildren"
      :id="`draggable_group_${unit.addressId}`"
      :list="sortedChildren"
      :disabled="!arrangeTreeMenu"
      :component-data="{ props: { parentAddressId: unit.addressId } }"
      @end="changeIndexEnd"
    >
      <tree-menu-v2
        v-for="node in sortedChildren"
        :key="node.id"
        :unit="node"
        :depth="depth + 1"
        :activate-menu="activateMenu"
        :reload-units="reloadUnits"
        :arrange-tree-menu="arrangeTreeMenu"
        :fuzzy-matched-address-ids="fuzzyMatchedAddressIds"
        :is-searching="isSearching"
      >
      </tree-menu-v2>
    </draggable>
  </div>
</template>

<script>
import { dayjs } from '../../../util/date'
import draggable from 'vuedraggable'
import ListIcon from './ListIcon.vue'
import Hashids from 'hashids'
import { blockClick } from '../../../util/longPressDirective'
const hashids = new Hashids()

export default {
  name: 'TreeMenuV2',
  components: {
    draggable,
    ListIcon
  },
  props: [
    'unit',
    'depth',
    'activateMenu',
    'reloadUnits',
    'arrangeTreeMenu',
    'fuzzyMatchedAddressIds',
    'isSearching'
  ],
  data() {
    return {
      showChildren: true,
      movingParams: null
    }
  },
  computed: {
    iconClasses() {
      return this.showChildren ? 'mdi-chevron-down' : 'mdi-chevron-right'
    },
    labelClasses() {
      return { 'has-children': this.unit.children }
    },
    indent() {
      return `padding-left: ${this.depth * 40}px`
    },
    indentMobile() {
      return `padding-left: ${this.depth * 17.5}px`
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    },
    bedId() {
      return this.unit.transientData && this.unit.transientData.bedId
    },
    supplementaryData() {
      const suppData =
        this.$store.getters['units/getPropertyUnitListSupplementaryDataMap']
      return suppData[String(this.bedId)]
    },
    isSupplementaryDataLoading() {
      return this.$store.getters[
        'units/isPropertyUnitListSupplementaryDataLoading'
      ]
    },
    paidUntilDate() {
      return this.supplementaryData.lease.paidUntilDate
    },
    nextRentalPeriod() {
      return this.supplementaryData.lease.nextRentalPeriod
    },
    tenant() {
      return this.supplementaryData.tenant
    },
    shouldShowRow() {
      if (!this.isSearching) {
        return true
      }

      return this.fuzzyMatchedAddressIds.some((addressId) =>
        addressId.includes(this.unit.addressId)
      )
    },
    bedStatus() {
      if (this.supplementaryData) {
        return this.supplementaryData.bed.status
      }
      return 'vacant'
    },
    sortedChildren() {
      if (!this.unit.children) return undefined

      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return this.unit.children.sort((a, b) => {
        return a.index - b.index
      })
    },
    linkedBedName() {
      if (this.bedStatus === 'linked') {
        const linkedBedId =
          this.supplementaryData.bed.parentUnit &&
          this.supplementaryData.bed.parentUnit.bedId

        const suppData =
          this.$store.getters['units/getPropertyUnitListSupplementaryDataMap']

        const linkedBedSuppData = suppData[String(linkedBedId)].bed.bedName

        return linkedBedSuppData
      }
      return ''
    }
  },
  methods: {
    toggleChildren() {
      this.showChildren = !this.showChildren
      if (Number.isInteger(this.bedId)) {
        this.redirectBed()
      }
    },
    redirectBed() {
      if (this.bedStatus === 'occupied') {
        this.$router.push('bed/' + hashids.encode(this.bedId))
      } else if (this.bedStatus === 'linked') {
        this.$router.push(
          'bed/' + hashids.encode(this.supplementaryData.linkedParentBedId)
        )
      }
    },
    concat(...arrays) {
      return [].concat(...arrays.filter(Array.isArray))
    },
    async changeIndexEnd(event) {
      this.$root.$setLoading(true)
      const newParentAddressId = event.to.id.split('draggable_group_')[1]
      const itemAddressId = event.item.id.split('tree_menu_')[1]

      const draggableEvent = {
        newIndex: event.newIndex,
        addressId: itemAddressId,
        newParentAddressId
      }

      // push the event to store
      try {
        await this.$store.dispatch('units/moveUnit', draggableEvent)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
      this.$root.$setLoading(false)
    },
    dueDateColor() {
      const today = dayjs().hour(0).minute(0).second(0).millisecond(0)
      if (today.isAfter(dayjs(this.paidUntilDate).subtract(1, 'day'))) {
        return 'body-2 font-weight-bold red--text'
      }
      return 'body-2 font-weight-bold'
    },
    isOverdue() {
      const today = dayjs().hour(0).minute(0).second(0).millisecond(0)
      return (
        today.isAfter(dayjs(this.paidUntilDate).subtract(1, 'day')) &&
        !this.isPaymentCompleted()
      )
    },
    getOverdueDate() {
      const date = this.paidUntilDate
      return dayjs(date).format('MMM D, YYYY')
    },
    isPaymentCompleted() {
      return this.getNextRentalStartDate() > this.supplementaryData.endOfLease
    },
    getNextRentalStartDate() {
      if (!this.supplementaryData) {
        return null
      }
      if (this.nextRentalPeriod && this.nextRentalPeriod.startDate) {
        return this.nextRentalPeriod.startDate
      } else {
        return this.paidUntilDate
      }
    },
    getUnitUser() {
      if (this.isSupplementaryDataLoading) {
        return 'Loading data...'
      }
      let name = '(empty)'
      if (!this.supplementaryData) {
        return name
      }

      if (this.supplementaryData.alias && this.supplementaryData.alias.name) {
        return this.supplementaryData.alias.name
      }

      if (this.bedStatus === 'linked' || this.bedStatus === 'linked_vacant') {
        name = `Linked to ${this.linkedBedName}`
      } else if (this.tenant) {
        name = ''
        if (this.tenant.firstName) {
          name += this.tenant.firstName + ' '
        }
        if (this.tenant.lastName) {
          name += this.tenant.lastName + ' '
        }
        if (this.tenant.username) {
          name += `(${this.tenant.username})`
        }
      }
      return name
    },
    onRowLongPress(e, unit) {
      this.activateMenu(e, unit)
      blockClick(window)
    }
  }
}
</script>

<style scoped>
body {
  font-family: 'Open Sans', sans-serif;
  font-size: 18px;
  font-weight: 300;
  line-height: 1em;
}

.container {
  width: 300px;
  margin: 0 auto;
}

.tree-menu {
  .label-wrapper {
    .has-children {
      cursor: pointer;
    }
  }
}
</style>
